import { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { HiOutlineLink, HiOutlineShare } from 'react-icons/hi';
import { t } from 'i18n';
import { absoluteUrl, productPath, storePath } from 'utils/storePaths';
import { formatPrice } from 'utils/formatPrice';

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** wa.me share link without a fixed recipient (the user picks the chat). */
export function whatsAppShareUrl(text) {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/** Store mode: share a product (native share sheet, WhatsApp, copy link). */
export default function ShareButtons({ product, storeName }) {
  const [copied, setCopied] = useState(false);
  if (!product) return null;
  const url = absoluteUrl(productPath(product));
  const text = t('storeMode.shareProductText', { name: product.name, price: formatPrice(product.price), url });
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const onShare = async () => {
    try {
      await navigator.share({ title: product.name, text: storeName ? `${product.name} · ${storeName}` : product.name, url });
    } catch {
      /* cancelled */
    }
  };
  const onCopy = async () => {
    if (await copyText(url)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };
  const btn = 'inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50';
  return (
    <div className="flex items-center gap-2" data-testid="product-share">
      {canNativeShare && (
        <button type="button" onClick={onShare} className={btn} aria-label={t('storeMode.share')}>
          <HiOutlineShare className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">{t('storeMode.share')}</span>
        </button>
      )}
      <a href={whatsAppShareUrl(text)} target="_blank" rel="noopener noreferrer" className={btn} aria-label={t('storeMode.shareWhatsapp')}>
        <FaWhatsapp className="h-4 w-4 text-emerald-600" aria-hidden />
        <span className="hidden sm:inline">{t('storeMode.whatsappShort')}</span>
      </a>
      <button type="button" onClick={onCopy} className={btn} aria-label={t('storeMode.copyLink')}>
        <HiOutlineLink className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">{copied ? t('storeMode.copied') : t('storeMode.copyLink')}</span>
      </button>
    </div>
  );
}

/** Vendor dashboard: "Your store link" card (copy + WhatsApp share). */
export function StoreLinkCard({ store }) {
  const [copied, setCopied] = useState(false);
  if (!store?.handle) return null;
  const url = absoluteUrl(storePath(store.handle));
  const text = t('storeMode.shareStoreText', { name: store.name, url });
  const onCopy = async () => {
    if (await copyText(url)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="store-link-card">
      <h2 className="text-sm font-semibold text-slate-900">{t('storeMode.yourLink')}</h2>
      <p className="mt-1 text-xs text-slate-500">{t('storeMode.yourLinkHelp')}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <a href={url} target="_blank" rel="noopener noreferrer" dir="ltr" className="min-w-0 flex-1 truncate rounded-lg bg-slate-50 px-3 py-2 font-mono text-sm text-slate-800" data-testid="store-link-url">
          {url}
        </a>
        <div className="flex gap-2">
          <button type="button" onClick={onCopy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">
            {copied ? t('storeMode.copied') : t('storeMode.copyLink')}
          </button>
          <a href={whatsAppShareUrl(text)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <FaWhatsapp className="h-4 w-4" aria-hidden />
            {t('storeMode.whatsappShort')}
          </a>
        </div>
      </div>
    </div>
  );
}
